import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Tile,
  Button,
  TextInput,
  Select,
  SelectItem,
  SkeletonText,
  SkeletonPlaceholder,
  InlineNotification,
  Tag,
  Search,
  ToastNotification,
  TextArea,
  Toggle
} from '@carbon/react';
import { Add, Filter, Close, Calendar, Task, Time, User, Group, ChevronLeft, ChevronRight, ChevronDown, Checkmark, CheckboxChecked } from '@carbon/icons-react';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import PageContainer from '../components/PageContainer';
import Collapsible from '../components/Collapsible';
import axiosInstance from '../utils/axiosInstance';
import { getApiErrorMessage, isConcurrencyConflictError } from '../utils/apiErrors';
import './Tasks.css';

// Nivo ozbiljnosti zadatka (backend severity: 1 = najviši, 3 = najniži nivo)
const SEVERITY_OPTIONS = [
  { value: 1, label: 'Veoma važno', tone: 'high' },
  { value: 2, label: 'Važno', tone: 'medium' },
  { value: 3, label: 'Normalno', tone: 'low' },
];

// Koraci čarobnjaka za ručno kreiranje zadatka
const ADD_TASK_STEPS = ['Osnovni podaci', 'Aktivnost', 'Dodela'];

// Broj aktivnosti po stranici u koraku 2 (backend /api/Activity ne poštuje
// pageNumber/pageSize, pa se straničenje radi lokalno - isto kao u Activities.js)
const ADD_TASK_ACTIVITY_PAGE_SIZE = 6;

// Broj zaposlenih po stranici u koraku 3
const ADD_TASK_EMPLOYEE_PAGE_SIZE = 20;

// Opcije za vreme izvršavanja - 24-časovni format (bez AM/PM), isto kao u Groups.js
const TASK_TIME_HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const TASK_TIME_MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

// ActivityType enum vrednosti (šalju se engleski nazivi, prikaz na srpskom).
// Backend prihvata case-insensitive nazive; nevalidna vrednost -> 400 InvalidActivityType.
const ACTIVITY_TYPES = [
  { value: 'Physical', label: 'Fizička aktivnost' },
  { value: 'Nutrition', label: 'Ishrana' },
  { value: 'Entertainment', label: 'Zabava' },
  { value: 'Rest', label: 'Odmor' },
  { value: 'Visit', label: 'Poseta' },
  { value: 'Medical', label: 'Medicinska aktivnost' },
  { value: 'Nursing', label: 'Nega' },
  { value: 'Hygiene', label: 'Higijena' },
];

const STATUS_LABELS = {
  waiting: 'Čeka izvršenje',
  in_progress: 'U toku',
  completed: 'Završen',
  canceled: 'Otkazan',
  cancelled: 'Otkazan',
};

const extractData = (res) => {
  const body = res.data;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  if (body && Array.isArray(body.value)) return body.value;
  if (body && Array.isArray(body.$values)) return body.$values;
  if (body && Array.isArray(body.items)) return body.items;
  if (body && body.data && Array.isArray(body.data.$values)) return body.data.$values;
  if (body && body.data && Array.isArray(body.data.items)) return body.data.items;
  return [];
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
};

// Boja tipa aktivnosti (isto kao u Groups.js / ResidentProfile.js)
const getActivityTypeColor = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'meal': case 'ishrana': case 'nutrition': return '#0f62fe';
    case 'therapy': case 'terapija': case 'medical': return '#da1e28';
    case 'exercise': case 'vezbe': case 'sport': case 'physical': return '#9fb746';
    case 'social': case 'socijalne': case 'entertainment': return '#8a3ffc';
    case 'visit': case 'poseta': return '#0f62fe';
    default: return '#6f6f6f';
  }
};

// Čitljiv naziv tipa aktivnosti na srpskom (ACTIVITY_TYPES drži engleske vrednosti)
const translateActivityType = (type) => {
  if (!type) return '';
  const match = ACTIVITY_TYPES.find(
    (item) => item.value.toLowerCase() === String(type).toLowerCase()
  );
  return match ? match.label : type;
};

// Nazivi tipova zaposlenih koji prate aktivnost - samo informativno u UI-ju.
// Pri kreiranju zadatka se NE šalju (backend ih izvlači iz activityId).
const getActivityEmployeeTypeNames = (activity) => {
  if (activity && Array.isArray(activity.employeeTypeNames)) {
    return activity.employeeTypeNames.filter(Boolean);
  }
  return [];
};

// yyyy-MM-dd u lokalnoj vremenskoj zoni (za <input type="date">)
const toLocalDateInput = (date) => {
  const d = date || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Izvlači čitljivu poruku o grešci iz API odgovora (ProblemDetails, errors mapa...).
// Poruke grešaka se izdvajaju zajedničkim helperom (utils/apiErrors), koji čita i
// ApiResponse (`message`) i ProblemDetails (`detail`/`title`) — uključujući 409
// `ConcurrencyConflict`.

// Returns display names for the task's employee types (employeeTypeNames is
// index-aligned with employeeTypeIds). Falls back to legacy singular field.
const getTaskEmployeeTypeNames = (task) => {
  if (!task) return '-';
  if (Array.isArray(task.employeeTypeNames) && task.employeeTypeNames.length > 0) {
    return task.employeeTypeNames.join(', ');
  }
  return task.employeeTypeName || '-';
};

// Novi backend format listi zadataka: `data` je lista kategorija
// [{ category, count, tasks }] - redosled dolazi sortiran sa backenda i ne menja se.
// Stari (ravni) format se i dalje toleriše kao jedna grupa bez kategorije.
const extractTaskGroups = (res) => {
  const list = extractData(res);
  if (list.length === 0) return [];
  const isGrouped = list.every((item) => item && Array.isArray(item.tasks));
  if (!isGrouped) {
    return [{ category: null, count: list.length, tasks: list }];
  }
  return list.map((group) => ({
    category: group.category || null,
    count: typeof group.count === 'number' ? group.count : group.tasks.length,
    tasks: group.tasks,
  }));
};

// Ravna lista zadataka iz grupa (za selekciju, deep-link, "označi sve"...).
const flattenTaskGroups = (groups) =>
  (Array.isArray(groups) ? groups : []).flatMap((group) =>
    Array.isArray(group?.tasks) ? group.tasks : []
  );

// Ključ grupe (kategorije) - koristi se za collapse/expand i za vidljivost.
const groupKey = (group, index) => group.category || `__all-${index}`;

// Svi ključevi kategorija iz učitanih grupa (za podrazumevano skupuljene kategorije).
const allGroupKeys = (groups) =>
  (Array.isArray(groups) ? groups : []).map((group, index) => groupKey(group, index));

const severityLabel = (severity) => {
  if (severity === 1) return 'Veoma Važno';
  if (severity === 2) return 'Važno';
  if (severity === 3) return 'Normalno';
  return 'Važno';
};

const severityClass = (severity) => {
  if (severity === 1) return 'high';
  if (severity === 2) return 'medium';
  if (severity === 3) return 'low';
  return 'medium';
};

// Završeni zadaci su već izvršeni - ne mogu da se označe/dodele.
const isSelectableTask = (task) => task?.status !== 'completed';

// Jedan red zadatka u listi. Memoizovan je kako promena selekcije (klik na checkbox)
// ne bi re-renderovala celu listu - klik tako nema primetno kašnjenje.
const TaskItemRow = React.memo(function TaskItemRow({ task, selectable, selected, onToggle, onOpen }) {
  // Završeni zadaci ne mogu da se selektuju (checkbox je onemogućen)
  const canSelect = selectable && isSelectableTask(task);
  return (
    <div
      className={`tasks-item tasks-item--severity-${severityClass(task.severity)} ${task.status === 'completed' ? 'tasks-item--completed' : ''} ${canSelect && selected ? 'tasks-item--selected' : ''}`}
      onClick={() => onOpen(task)}
    >
      {selectable && (
        <button
          type="button"
          className={`tasks-item__checkbox ${canSelect && selected ? 'tasks-item__checkbox--checked' : ''} ${canSelect ? '' : 'tasks-item__checkbox--disabled'}`}
          disabled={!canSelect}
          title={canSelect ? undefined : 'Završen zadatak ne može da se dodeli'}
          onClick={(e) => {
            if (!canSelect) return;
            e.stopPropagation();
            onToggle(task.id);
          }}
          aria-label={
            canSelect
              ? (selected ? 'Odaberi zadatak' : 'Poništi izbor zadatka')
              : 'Završen zadatak ne može da se dodeli'
          }
          aria-pressed={selected}
        >
          {canSelect && selected && <Checkmark size={16} />}
        </button>
      )}
      <div className="tasks-item__content">
        <span className="tasks-item__title">{task.title}</span>
        <div className="tasks-item__meta">
          {/* Zadaci kreirani sa assignToAll = true se prikazuju kao
              "Dodeljeno svima" (bez imena pojedinačnog zaposlenog). */}
          {task.assignToAll ? (
            <span className="tasks-item__meta-item tasks-item__meta-item--all">
              <Group size={14} />
              Dodeljeno svima
            </span>
          ) : task.assignedToName ? (
            <span className="tasks-item__meta-item">
              <User size={14} />
              {task.assignedToName}
            </span>
          ) : null}
          {task.residentName && (
            <span className="tasks-item__meta-item">
              <User size={14} />
              {task.residentName}
            </span>
          )}
          {task.mealTypeName && (
            <span className="tasks-item__meta-item">
              <Task size={14} />
              {task.mealTypeName}
            </span>
          )}
          {task.scheduledTime && (
            <span className="tasks-item__meta-item">
              <Time size={14} />
              {formatTime(task.scheduledTime)}
            </span>
          )}
        </div>
      </div>
      <span className={`tasks-status tasks-status--${task.status}`}>
        {STATUS_LABELS[task.status] || task.status}
      </span>
    </div>
  );
});

const Tasks = ({ endpoint = '/api/Task/by-date', showAddTask = true, selectable = true, collapseCategoriesByDefault = true }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId: routeTaskId } = useParams();
  const deepLinkHandledRef = useRef(null);

  // Base path of the current tasks page (e.g. /tasks or /caregiver-tasks),
  // used to build the /{base}/{taskId} deep-link URL.
  const basePath = '/' + (location.pathname.split('/').filter(Boolean)[0] || 'tasks');

  // Zadaci dolaze grupisani po kategorijama (backend radi i sortiranje i grupisanje).
  const [taskGroups, setTaskGroups] = useState([]);
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);

  // Filters
  const [activityTypeFilter, setActivityTypeFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  // assigned: 0 = svi, 1 = samo dodeljeni, 2 = samo nedodeljeni
  const [assignedFilter, setAssignedFilter] = useState('0');
  const [areas, setAreas] = useState([]);

  // Mobile panels
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterExiting, setIsFilterExiting] = useState(false);

  // Da li je PC prikaz (>= 769px). Na PC-u su lebdeći buttone "Označi sve" i
  // "Dodeli" vidljivi stalno, a na mobilnom se prikazuju uz selekciju.
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 769px)').matches
      : true
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(min-width: 769px)');
    const handleChange = (event) => setIsDesktopView(event.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Stariji browseri (Safari < 14)
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  // Right side panel (task details)
  const [selectedTask, setSelectedTask] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Task comments (notes)
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [isNotesExpanded, setIsNotesExpanded] = useState(true);

  // Assign employee side panel
  const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignEmployees, setAssignEmployees] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignToast, setAssignToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const [assignPage, setAssignPage] = useState(1);
  const [assignTotalPages, setAssignTotalPages] = useState(1);
  const [assignHasNext, setAssignHasNext] = useState(false);
  const [assignHasPrev, setAssignHasPrev] = useState(false);
  const [assignPageFading, setAssignPageFading] = useState(false);

  // Add task side panel (3-koračni čarobnjak: podaci -> aktivnost/oblast/severity -> dodela)
  const [isAddTaskPanelOpen, setIsAddTaskPanelOpen] = useState(false);
  const [addTaskStep, setAddTaskStep] = useState(0);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [addTaskError, setAddTaskError] = useState(null);
  const [addTaskToast, setAddTaskToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);

  // Korak 1 - osnovni podaci
  const [addTaskTitle, setAddTaskTitle] = useState('');
  const [addTaskDescription, setAddTaskDescription] = useState('');
  const [addTaskDate, setAddTaskDate] = useState('');
  const [addTaskTime, setAddTaskTime] = useState('09:00');

  // Korak 2 - aktivnost, oblast i nivo ozbiljnosti
  const [addTaskActivities, setAddTaskActivities] = useState([]);
  const [addTaskActivitiesLoading, setAddTaskActivitiesLoading] = useState(false);
  const [addTaskActivitiesError, setAddTaskActivitiesError] = useState(null);
  const [addTaskActivitySearch, setAddTaskActivitySearch] = useState('');
  const [addTaskActivityPage, setAddTaskActivityPage] = useState(1);
  const [addTaskActivityPageFading, setAddTaskActivityPageFading] = useState(false);
  const [addTaskActivityId, setAddTaskActivityId] = useState('');
  const [addTaskAreaId, setAddTaskAreaId] = useState('');
  const [addTaskSeverity, setAddTaskSeverity] = useState(3);

  // Korak 3 - dodela (svima ili jednom zaposlenom)
  const [addTaskAssignToAll, setAddTaskAssignToAll] = useState(true);
  const [addTaskEmployee, setAddTaskEmployee] = useState(null);
  const [addTaskEmployees, setAddTaskEmployees] = useState([]);
  const [addTaskEmployeesLoading, setAddTaskEmployeesLoading] = useState(false);
  const [addTaskEmployeesError, setAddTaskEmployeesError] = useState(null);
  const [addTaskEmployeeSearch, setAddTaskEmployeeSearch] = useState('');
  const [addTaskEmployeePage, setAddTaskEmployeePage] = useState(1);
  const [addTaskEmployeeTotalPages, setAddTaskEmployeeTotalPages] = useState(1);
  const [addTaskEmployeeHasNext, setAddTaskEmployeeHasNext] = useState(false);
  const [addTaskEmployeeHasPrev, setAddTaskEmployeeHasPrev] = useState(false);
  const [addTaskEmployeePageFading, setAddTaskEmployeePageFading] = useState(false);

  // Fetch areas for the area filter dropdown
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const res = await axiosInstance.get('/api/Area');
        setAreas(extractData(res));
      } catch (err) {
        console.error('Greška pri učitavanju oblasti:', err);
      }
    };

    fetchAreas();
  }, []);

  // Fetch tasks for selected date (bez paging-a - backend vraća grupisano po kategorijama)
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.append('date', selectedDate);
        if (activityTypeFilter) params.append('activityType', activityTypeFilter);
        if (areaFilter) params.append('areaId', areaFilter);
        if (assignedFilter) params.append('assigned', assignedFilter);
        const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
        const groups = extractTaskGroups(response);
        setTaskGroups(groups);
        // Podrazumevano (admin /tasks): sve kategorije su skupuljene kada se lista učita.
        setCollapsedCategories(collapseCategoriesByDefault ? new Set(allGroupKeys(groups)) : new Set());
      } catch (err) {
        console.error('Greška pri učitavanju zadataka:', err);
        setError('Nije moguće učitati zadatke. Pokušajte ponovo.');
        setTaskGroups([]);
        setCollapsedCategories(new Set());
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [selectedDate, activityTypeFilter, areaFilter, assignedFilter, endpoint, tasksRefreshKey, collapseCategoriesByDefault]);

  // Ravna lista svih zadataka (ista referenca dok se grupe ne promene).
  const tasks = useMemo(() => flattenTaskGroups(taskGroups), [taskGroups]);

  // Set za O(1) proveru selekcije (lista može imati veliki broj zadataka).
  const selectedTaskIdSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds]);

  // Zadaci iz razvijenih (expanded) kategorija - samo oni ulaze u "Označi sve".
  // Zadatke iz skupuljenih kategorija ne uzimamo u obzir.
  const visibleTasks = useMemo(
    () =>
      flattenTaskGroups(
        taskGroups.filter((group, index) => !collapsedCategories.has(groupKey(group, index)))
      ),
    [taskGroups, collapsedCategories]
  );

  // Vidljivi zadaci koji MOGU da se dodele (završeni se preskaču).
  const selectableVisibleTasks = useMemo(
    () => visibleTasks.filter(isSelectableTask),
    [visibleTasks]
  );

  // Ako neki označeni zadatak postane završen (npr. zaposleni ga završi),
  // skloni ga iz selekcije - završeni zadaci se ne dodeljuju.
  useEffect(() => {
    const nonSelectableIds = new Set(
      tasks.filter((task) => !isSelectableTask(task)).map((task) => task.id)
    );
    if (nonSelectableIds.size === 0) return;
    setSelectedTaskIds((prev) => {
      const next = prev.filter((id) => !nonSelectableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [tasks]);

  // Skupi/razvi kategoriju (naslov kategorije je ključ).
  // Pri skupljanju kategorije, njeni zadaci se sklanjaju iz selekcije.
  const toggleCategory = (key) => {
    const willCollapse = !collapsedCategories.has(key);
    if (willCollapse) {
      const group = taskGroups.find((item, index) => groupKey(item, index) === key);
      const ids = new Set(Array.isArray(group?.tasks) ? group.tasks.map((t) => t.id) : []);
      if (ids.size > 0) {
        setSelectedTaskIds((prev) => prev.filter((id) => !ids.has(id)));
      }
    }
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // ===========================================
  // Add task wizard (right side panel)
  // Korak 1: naslov, opis, datum i vreme
  // Korak 2: aktivnost, oblast i nivo ozbiljnosti
  // Korak 3: dodela svima ili jednom zaposlenom
  // ===========================================

  // Vraća čarobnjak na početno stanje (korak 1, prazna forma)
  const resetAddTaskForm = () => {
    setAddTaskStep(0);
    setAddTaskError(null);
    setAddTaskTitle('');
    setAddTaskDescription('');
    setAddTaskDate(toLocalDateInput());
    setAddTaskTime('09:00');
    setAddTaskActivitiesError(null);
    setAddTaskActivitySearch('');
    setAddTaskActivityPage(1);
    setAddTaskActivityPageFading(false);
    setAddTaskActivityId('');
    setAddTaskAreaId(areaFilter || '');
    setAddTaskSeverity(3);
    setAddTaskAssignToAll(true);
    setAddTaskEmployee(null);
    setAddTaskEmployees([]);
    setAddTaskEmployeesError(null);
    setAddTaskEmployeeSearch('');
    setAddTaskEmployeePage(1);
  };

  const openAddTaskPanel = () => {
    resetAddTaskForm();
    setIsAddTaskPanelOpen(true);
    fetchAddTaskActivities();
  };

  const closeAddTaskPanel = () => {
    setIsAddTaskPanelOpen(false);
    setIsCreatingTask(false);
    setAddTaskError(null);
    setAddTaskStep(0);
    setAddTaskEmployees([]);
    setAddTaskEmployee(null);
  };

  // Prelazak između koraka uz validaciju prethodnog koraka
  const goToAddTaskStep = (step) => {
    if (step === addTaskStep) return;
    if (step > 0 && !addTaskTitle.trim()) {
      setAddTaskError('Unesite naslov zadatka.');
      return;
    }
    if (step > 0 && (!addTaskDate || !addTaskTime)) {
      setAddTaskError('Datum i vreme izvršavanja su obavezni.');
      return;
    }
    if (step > 1 && !addTaskActivityId) {
      setAddTaskError('Izaberite aktivnost.');
      return;
    }
    setAddTaskError(null);
    setAddTaskStep(step);
    if (step === 2 && !addTaskAssignToAll && addTaskEmployees.length === 0) {
      fetchAddTaskEmployees('', 1);
    }
  };

  const fetchAddTaskActivities = async () => {
    setAddTaskActivitiesLoading(true);
    setAddTaskActivitiesError(null);
    try {
      const response = await axiosInstance.get('/api/Activity');
      setAddTaskActivities(extractData(response));
    } catch (err) {
      console.error('Greška pri učitavanju aktivnosti:', err);
      setAddTaskActivitiesError('Nije moguće učitati aktivnosti. Pokušajte ponovo.');
      setAddTaskActivities([]);
    } finally {
      setAddTaskActivitiesLoading(false);
    }
  };

  // Pretraga aktivnosti i straničenje se rade lokalno - backend /api/Activity
  // ne poštuje pageNumber/pageSize (isti pristup kao u Activities.js).
  const filteredAddTaskActivities = useMemo(() => {
    const term = addTaskActivitySearch.trim().toLowerCase();
    if (!term) return addTaskActivities;
    return addTaskActivities.filter((activity) => (
      String(activity.name || '').toLowerCase().includes(term) ||
      String(activity.activityType || '').toLowerCase().includes(term) ||
      translateActivityType(activity.activityType).toLowerCase().includes(term)
    ));
  }, [addTaskActivities, addTaskActivitySearch]);

  const addTaskActivityTotalPages = Math.max(
    1,
    Math.ceil(filteredAddTaskActivities.length / ADD_TASK_ACTIVITY_PAGE_SIZE)
  );
  const addTaskActivityCurrentPage = Math.min(addTaskActivityPage, addTaskActivityTotalPages);
  const addTaskActivitiesOnPage = filteredAddTaskActivities.slice(
    (addTaskActivityCurrentPage - 1) * ADD_TASK_ACTIVITY_PAGE_SIZE,
    addTaskActivityCurrentPage * ADD_TASK_ACTIVITY_PAGE_SIZE
  );

  const selectedAddTaskActivity =
    addTaskActivities.find((activity) => String(activity.id) === String(addTaskActivityId)) || null;
  const selectedAddTaskArea =
    areas.find((area) => String(area.id) === String(addTaskAreaId)) || null;
  const selectedAddTaskSeverity =
    SEVERITY_OPTIONS.find((option) => option.value === addTaskSeverity) || SEVERITY_OPTIONS[2];

  // Straničenje liste aktivnosti (isti pattern kao u Groups/ResidentProfile)
  const nextAddTaskActivityPage = () => {
    if (addTaskActivityCurrentPage >= addTaskActivityTotalPages) return;
    setAddTaskActivityPageFading(true);
    setTimeout(() => {
      setAddTaskActivityPage(prev => prev + 1);
      setAddTaskActivityPageFading(false);
    }, 300);
  };

  const prevAddTaskActivityPage = () => {
    if (addTaskActivityCurrentPage <= 1) return;
    setAddTaskActivityPageFading(true);
    setTimeout(() => {
      setAddTaskActivityPage(prev => prev - 1);
      setAddTaskActivityPageFading(false);
    }, 300);
  };

  const fetchAddTaskEmployees = async (search, pageNumber) => {
    setAddTaskEmployeesLoading(true);
    setAddTaskEmployeesError(null);
    try {
      const { employees, totalPages, hasNextPage, hasPreviousPage } = await fetchEmployeesPage(
        search,
        pageNumber,
        ADD_TASK_EMPLOYEE_PAGE_SIZE
      );
      setAddTaskEmployees(employees);
      setAddTaskEmployeeTotalPages(totalPages);
      setAddTaskEmployeeHasNext(hasNextPage);
      setAddTaskEmployeeHasPrev(hasPreviousPage);
    } catch (err) {
      console.error('Greška pri učitavanju zaposlenih:', err);
      setAddTaskEmployeesError('Nije moguće učitati zaposlene. Pokušajte ponovo.');
      setAddTaskEmployees([]);
    } finally {
      setAddTaskEmployeesLoading(false);
    }
  };

  const handleAddTaskEmployeeSearch = (value) => {
    setAddTaskEmployeeSearch(value);
    setAddTaskEmployeePage(1);
    fetchAddTaskEmployees(value, 1);
  };

  const nextAddTaskEmployeePage = () => {
    if (!addTaskEmployeeHasNext) return;
    setAddTaskEmployeePageFading(true);
    setTimeout(() => {
      setAddTaskEmployeePage(prev => prev + 1);
      fetchAddTaskEmployees(addTaskEmployeeSearch, addTaskEmployeePage + 1);
      setAddTaskEmployeePageFading(false);
    }, 300);
  };

  const prevAddTaskEmployeePage = () => {
    if (!addTaskEmployeeHasPrev) return;
    setAddTaskEmployeePageFading(true);
    setTimeout(() => {
      setAddTaskEmployeePage(prev => prev - 1);
      fetchAddTaskEmployees(addTaskEmployeeSearch, addTaskEmployeePage - 1);
      setAddTaskEmployeePageFading(false);
    }, 300);
  };

  // Prebacivanje "Dodeli svima" (on/off) - kada je isključeno, bira se zaposleni
  const handleAddTaskAssignAllToggle = (toggled) => {
    setAddTaskAssignToAll(toggled);
    setAddTaskEmployee(null);
    if (!toggled && addTaskEmployees.length === 0) {
      fetchAddTaskEmployees('', 1);
    }
  };

  // POST /api/Task - kreira zadatak i prikazuje odgovarajuću notifikaciju
  const handleCreateTask = async () => {
    if (!addTaskTitle.trim()) {
      setAddTaskError('Unesite naslov zadatka.');
      setAddTaskStep(0);
      return;
    }
    if (!addTaskDate || !addTaskTime) {
      setAddTaskError('Datum i vreme izvršavanja su obavezni.');
      setAddTaskStep(0);
      return;
    }
    if (!addTaskActivityId) {
      setAddTaskError('Izaberite aktivnost.');
      setAddTaskStep(1);
      return;
    }
    if (!addTaskAssignToAll && !addTaskEmployee) {
      setAddTaskError('Izaberite zaposlenog ili uključite opciju "Dodeli svima".');
      return;
    }

    const scheduledTime = new Date(`${addTaskDate}T${addTaskTime}`);
    if (Number.isNaN(scheduledTime.getTime())) {
      setAddTaskError('Datum ili vreme nisu u ispravnom formatu.');
      return;
    }

    setAddTaskError(null);
    setIsCreatingTask(true);
    try {
      // Novi body za POST /api/Task: activityType, employeeTypeIds, resident*,
      // roomId, status i mealType se NE šalju (backend ih izvlači iz activityId).
      const payload = {
        title: addTaskTitle.trim(),
        description: addTaskDescription.trim(),
        scheduledTime: scheduledTime.toISOString(),
        activityId: addTaskActivityId,
        assignToAll: addTaskAssignToAll,
        severity: addTaskSeverity
      };

      // Opciona polja šaljemo samo kada imaju vrednost.
      if (!addTaskAssignToAll && addTaskEmployee) {
        payload.assignedToId = addTaskEmployee.id;
      }
      if (addTaskAreaId) {
        payload.areaId = addTaskAreaId;
      }

      // Response je ApiResponse<TaskDto[]> - 1 zadatak za običnu dodelu,
      // N zadataka (po jedan po zaposlenom) kada je assignToAll = true.
      const response = await axiosInstance.post('/api/Task', payload);
      const createdCount = extractData(response).length || 1;

      setAddTaskToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: addTaskAssignToAll
          ? `Zadatak "${payload.title}" je kreiran i dodeljen svim zaposlenima (${createdCount} ${createdCount === 1 ? 'zadatak' : 'zadataka'})`
          : `Zadatak "${payload.title}" je kreiran i dodeljen zaposlenom ${addTaskEmployee.firstName} ${addTaskEmployee.lastName}`
      });
      setTimeout(() => setAddTaskToast(prev => ({ ...prev, show: false })), 4000);

      closeAddTaskPanel();

      // Prikaži novi zadatak u listi: prebaci na njegov datum i osveži listu
      if (addTaskDate !== selectedDate) {
        setSelectedDate(addTaskDate);
      }
      setTasksRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Greška pri kreiranju zadatka:', err);
      const message = getApiErrorMessage(err, 'Nije moguće kreirati zadatak. Pokušajte ponovo.');
      setAddTaskToast({ show: true, kind: 'error', title: 'Greška', subtitle: message });
      setTimeout(() => setAddTaskToast(prev => ({ ...prev, show: false })), 4000);
    } finally {
      setIsCreatingTask(false);
    }
  };

  const fetchNotes = useCallback(async (taskId) => {
    if (!taskId) return;
    setNotesLoading(true);
    setNotesError(null);
    try {
      const response = await axiosInstance.get(`/api/TaskNote/task/${taskId}`);
      const body = response.data;
      const payload = body && body.data ? body.data : body;
      setNotes(Array.isArray(payload) ? payload : []);
    } catch (err) {
      // 404 (nema komentara) nije greška - prikaži prazno stanje
      if (err?.response?.status === 404) {
        setNotes([]);
      } else {
        console.error('Greška pri učitavanju komentara:', err);
        setNotesError('Greška pri učitavanju komentara');
      }
    } finally {
      setNotesLoading(false);
    }
  }, []);

  // Stabilna referenca (memoizovani redovi je koriste kao prop).
  const openTaskDetails = useCallback((task) => {
    setSelectedTask(task);
    setIsSidePanelOpen(true);
    setNotes([]);
    setNotesError(null);
    setIsNotesExpanded(true);
    fetchNotes(task?.id);
    // Reflect the selected task in the URL: /{base}/{taskId}
    if (task?.id != null && String(task.id) !== String(routeTaskId)) {
      deepLinkHandledRef.current = String(task.id);
      navigate(`${basePath}/${task.id}`);
    }
  }, [fetchNotes, navigate, basePath, routeTaskId]);

  // Deep-link: open a task's details when the URL contains /{base}/{taskId}
  // (e.g. from a notification or a direct link).
  useEffect(() => {
    if (!routeTaskId) {
      deepLinkHandledRef.current = null;
      return;
    }
    if (deepLinkHandledRef.current === String(routeTaskId)) return;

    const loadTask = async () => {
      setNotes([]);
      setNotesError(null);
      setIsNotesExpanded(true);
      fetchNotes(routeTaskId);

      // Prefer the already-loaded list item to avoid an extra request.
      const match = tasks.find((t) => String(t.id) === String(routeTaskId));
      if (match) {
        deepLinkHandledRef.current = String(routeTaskId);
        setSelectedTask(match);
        setIsSidePanelOpen(true);
        return;
      }

      // Otherwise fetch the task directly by id.
      try {
        const response = await axiosInstance.get(`/api/Task/${routeTaskId}`);
        const body = response.data;
        const payload = body && body.data ? body.data : body;
        if (payload) {
          deepLinkHandledRef.current = String(routeTaskId);
          setSelectedTask(payload);
          setIsSidePanelOpen(true);
        }
      } catch (err) {
        console.error('Greška pri učitavanju detalja zadatka:', err);
      }
    };

    loadTask();
  }, [routeTaskId, tasks, fetchNotes]);

  // Označi/poništi sve VIDLJIVE zadatke (samo razvijene kategorije).
  // Zadatke iz skupuljenih kategorija ne uzimamo u obzir.
  // Završeni zadaci se preskaču - njih nije moguće dodeliti.
  const toggleSelectAllTasks = () => {
    const listIds = selectableVisibleTasks.map((t) => t.id);
    if (listIds.length === 0) return;
    setSelectedTaskIds(prev => {
      const allSelected = listIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !listIds.includes(id));
      }
      const merged = new Set(prev);
      listIds.forEach(id => merged.add(id));
      return Array.from(merged);
    });
  };

  const closePanel = () => {
    setIsSidePanelOpen(false);
    setSelectedTask(null);
    setNotes([]);
    setNotesError(null);
    setIsNotesExpanded(true);
    deepLinkHandledRef.current = null;
    // Return to the list URL when a task was open via deep-link.
    if (routeTaskId) {
      navigate(basePath, { replace: true });
    }
  };

  const openAssignPanel = () => {
    setAssignSearch('');
    setAssignError(null);
    setAssignPage(1);
    setIsAssignPanelOpen(true);
    fetchAssignEmployees('', 1);
  };

  const closeAssignPanel = () => {
    setIsAssignPanelOpen(false);
    setAssignEmployees([]);
  };

  // Zajedničko učitavanje strane zaposlenih - koristi ga panel za dodelu
  // postojećih zadataka i korak 3 čarobnjaka za kreiranje zadatka.
  const fetchEmployeesPage = async (search, pageNumber, pageSize) => {
    const params = new URLSearchParams();
    params.append('pageNumber', String(pageNumber));
    params.append('pageSize', String(pageSize));
    if (search) params.append('search', search);
    const response = await axiosInstance.get(`/api/Employee?${params.toString()}`);
    const data = extractData(response);

    // Pagination metadata
    const body = response.data;
    const pagination =
      body?.pagination ||
      body?.data?.pagination ||
      body?.value?.pagination ||
      body?.$values?.pagination ||
      body?.items?.pagination ||
      body?.meta ||
      body?.data?.meta;

    let totalPages = 1;
    if (pagination?.totalPages) {
      totalPages = pagination.totalPages;
    } else if (pagination?.totalCount) {
      totalPages = Math.ceil(pagination.totalCount / pageSize) || 1;
    } else if (pagination?.totalItems) {
      totalPages = Math.ceil(pagination.totalItems / pageSize) || 1;
    } else {
      totalPages = Math.ceil(data.length / pageSize) || 1;
    }

    return {
      employees: data,
      totalPages,
      hasNextPage: pagination?.hasNextPage ?? pageNumber < totalPages,
      hasPreviousPage: pagination?.hasPreviousPage ?? pageNumber > 1,
    };
  };

  const fetchAssignEmployees = async (search, pageNumber) => {
    setAssignLoading(true);
    setAssignError(null);
    try {
      const { employees, totalPages, hasNextPage, hasPreviousPage } =
        await fetchEmployeesPage(search, pageNumber, 50);
      setAssignEmployees(employees);
      setAssignTotalPages(totalPages);
      setAssignHasNext(hasNextPage);
      setAssignHasPrev(hasPreviousPage);
    } catch (err) {
      console.error('Greška pri učitavanju zaposlenih:', err);
      setAssignError('Nije moguće učitati zaposlene. Pokušajte ponovo.');
      setAssignEmployees([]);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAssignSearchChange = (value) => {
    setAssignSearch(value);
    setAssignPage(1);
    fetchAssignEmployees(value, 1);
  };

  const nextAssignPage = () => {
    if (assignHasNext) {
      setAssignPageFading(true);
      setTimeout(() => {
        setAssignPage(prev => prev + 1);
        fetchAssignEmployees(assignSearch, assignPage + 1);
        setAssignPageFading(false);
      }, 300);
    }
  };

  const prevAssignPage = () => {
    if (assignHasPrev) {
      setAssignPageFading(true);
      setTimeout(() => {
        setAssignPage(prev => prev - 1);
        fetchAssignEmployees(assignSearch, assignPage - 1);
        setAssignPageFading(false);
      }, 300);
    }
  };

  const handleAssignEmployee = async (employee) => {
    // Determine which tasks to assign: selected tasks if any, otherwise the currently open task
    const taskIds = selectedTaskIds.length > 0
      ? selectedTaskIds
      : (selectedTask ? [selectedTask.id] : []);
    if (taskIds.length === 0) return;
    setAssigning(true);
    setAssignError(null);
    try {
      await axiosInstance.post('/api/Task/assign', {
        taskIds,
        employeeId: employee.id
      });
      setAssignToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: `${taskIds.length} ${taskIds.length === 1 ? 'zadatak je' : 'zadataka je'} dodeljen zaposlenom ${employee.firstName} ${employee.lastName}`
      });
      setTimeout(() => setAssignToast(prev => ({ ...prev, show: false })), 4000);
      closeAssignPanel();
      // Clear selection after assignment
      setSelectedTaskIds([]);
      // Refresh tasks list to reflect the assignment
      const assignedName = `${employee.firstName} ${employee.lastName}`;
      setTaskGroups(prev => prev.map(group => ({
        ...group,
        tasks: group.tasks.map(t =>
          taskIds.includes(t.id)
            ? { ...t, assignedToId: employee.id, assignedToName: assignedName }
            : t
        )
      })));
      // Refresh currently open task details
      setSelectedTask(prev => prev ? { ...prev, assignedToId: employee.id, assignedToName: assignedName } : prev);
    } catch (err) {
      console.error('Greška pri dodeljivanju zadatka:', err);

      if (isConcurrencyConflictError(err)) {
        // Neko je u međuvremenu izmenio zadatke — osveži listu da prikaz bude tačan.
        setTasksRefreshKey((prev) => prev + 1);
      }

      setAssignError(getApiErrorMessage(err, 'Nije moguće dodeliti zadatak. Pokušajte ponovo.'));
    } finally {
      setAssigning(false);
    }
  };

  const handleCloseMobileFilter = () => {
    setIsFilterExiting(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterExiting(false);
    }, 300);
  };

  // Da li su svi VIDLJIVI zadaci (razvijene kategorije) označeni - za "Označi sve" toggle.
  // Završeni zadaci se ne računaju (nisu selektabilni).
  const allTasksSelected =
    selectableVisibleTasks.length > 0 &&
    selectableVisibleTasks.every((t) => selectedTaskIds.includes(t.id));

  // Stabilan handler za checkbox - funkcionalni setState znači da se referenca ne
  // menja između rendera, pa memoizovani redovi ne re-renderuju bez potrebe.
  const handleToggleTask = useCallback((taskId) => {
    setSelectedTaskIds(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  }, []);

  return (
    <PageContainer>
      <div className="tasks-container">
          <Tile style={{ padding: '1.5rem', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'transparent', boxShadow: 'none' }}>
            {/* Toolbar: date picker + pagination + desktop add form */}
            <div className="tasks-toolbar">
              <div className="tasks-date-group">
                <div className="tasks-date-picker">
                  <Calendar size={18} />
                  <input
                    type="date"
                    className="tasks-date-input"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    aria-label="Izaberite datum"
                  />
                </div>

                {/* Add task button (circular) */}
                {showAddTask && (
                  <button
                    type="button"
                    className="tasks-add-round-btn"
                    onClick={openAddTaskPanel}
                    aria-label="Dodaj zadatak"
                    title="Dodaj zadatak"
                  >
                    <Add size={20} />
                  </button>
                )}
              </div>

            </div>

            {/* Error notification */}
            {error && (
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={error}
                onCloseButtonClick={() => setError(null)}
                lowContrast
                style={{ marginBottom: '1rem' }}
              />
            )}

            {/* Task list */}
            <div className="tasks-list">
              {loading ? (
                <div className="tasks-skeleton">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="tasks-skeleton-item">
                      <SkeletonText width="60%" />
                      <SkeletonText width="30%" />
                    </div>
                  ))}
                </div>
              ) : tasks.length === 0 ? (
                <div className="tasks-empty">
                  <div className="empty-icon">📋</div>
                  <p className="empty-title">Nema zadataka</p>
                  <p className="empty-subtitle">
                    Nema zadataka za izabrani datum
                  </p>
                </div>
              ) : (
                <div className="tasks-groups">
                  {taskGroups.map((group, index) => {
                    const key = groupKey(group, index);
                    const isCollapsed = collapsedCategories.has(key);
                    return (
                      <div key={key} className="tasks-group">
                        {group.category && (
                          <button
                            type="button"
                            className={`tasks-group__header ${isCollapsed ? '' : 'is-expanded'}`}
                            onClick={() => toggleCategory(key)}
                            aria-expanded={!isCollapsed}
                          >
                            <span className="tasks-group__title">{group.category}</span>
                            <span className="tasks-group__count">{group.tasks.length}</span>
                            <ChevronDown
                              size={16}
                              className={`tasks-group__chevron ${isCollapsed ? '' : 'is-expanded'}`}
                            />
                          </button>
                        )}
                        <Collapsible open={!isCollapsed}>
                          <div className="tasks-group__items">
                            {group.tasks.map((task) => (
                              <TaskItemRow
                                key={task.id}
                                task={task}
                                selectable={selectable}
                                selected={selectedTaskIdSet.has(task.id)}
                                onToggle={handleToggleTask}
                                onOpen={openTaskDetails}
                              />
                            ))}
                          </div>
                        </Collapsible>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Tile>
      </div>

      {/* Right Side Panel - Task Details */}
      <div className={`tasks-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Detalji zadatka
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={closePanel}
          />
        </div>

        <div className="side-panel-content">
          {selectedTask && (
            <div className="task-details">
              <div className="task-details__header">
                <div className="task-details__icon">
                  <Task size={28} />
                </div>
                <h2 className="task-details__title">{selectedTask.title}</h2>
              </div>

              <div className="task-details__tags">
                <Tag type="blue" size="sm">
                  {selectedTask.activityTypeName || selectedTask.activityType || 'Zadatak'}
                </Tag>
                {selectedTask.assignToAll && (
                  <Tag type="outline" size="sm">Dodeljeno svima</Tag>
                )}
                <span className={`tasks-priority tasks-priority--${severityClass(selectedTask.severity)}`}>
                  {severityLabel(selectedTask.severity)}
                </span>
                <span className={`tasks-status tasks-status--${selectedTask.status}`}>
                  {STATUS_LABELS[selectedTask.status] || selectedTask.status}
                </span>
              </div>

              {selectedTask.description && (
                <div className="task-details__section">
                  <h4 className="task-details__label">Opis</h4>
                  <p className="task-details__value">{selectedTask.description}</p>
                </div>
              )}

              <div className="task-details__section">
                <h4 className="task-details__label">Informacije</h4>
                <div className="task-details__info">
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Korisnik</span>
                    <span className="task-details__info-value">{selectedTask.residentName || '-'}</span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Tip aktivnosti</span>
                    <span className="task-details__info-value">
                      {selectedTask.activityTypeName || selectedTask.activityType || '-'}
                    </span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Zakazano vreme</span>
                    <span className="task-details__info-value">
                      {selectedTask.scheduledTime ? formatDate(selectedTask.scheduledTime) + ' ' + formatTime(selectedTask.scheduledTime) : '-'}
                    </span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Zadužena osoba</span>
                    <span className="task-details__info-value">
                      {selectedTask.assignToAll ? (
                        'Svi zaposleni'
                      ) : selectedTask.assignedToId ? (
                        selectedTask.assignedToName ? (
                          <Link
                            to={`/employees/${selectedTask.assignedToId}`}
                            className="task-details__employee-link"
                          >
                            {selectedTask.assignedToName}
                          </Link>
                        ) : '-'
                      ) : selectable && isSelectableTask(selectedTask) ? (
                        <button
                          type="button"
                          className="task-details__employee-type-link"
                          onClick={openAssignPanel}
                        >
                          Dodeli
                        </button>
                      ) : (
                        '-'
                      )}
                    </span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Prati</span>
                    <span className="task-details__info-value">{getTaskEmployeeTypeNames(selectedTask)}</span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Status</span>
                    <span className="task-details__info-value">{STATUS_LABELS[selectedTask.status] || selectedTask.status}</span>
                  </div>
                  {selectedTask.executedAt && (
                    <div className="task-details__info-row">
                      <span className="task-details__info-label">Izvršeno</span>
                      <span className="task-details__info-value">
                        {formatDate(selectedTask.executedAt)} {formatTime(selectedTask.executedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Komentari vezani za zadatak (expand/collapse) */}
              <div className="task-details__section">
                <button
                  type="button"
                  className={`task-details__notes-toggle ${isNotesExpanded ? 'is-expanded' : ''}`}
                  onClick={() => setIsNotesExpanded((prev) => !prev)}
                  aria-expanded={isNotesExpanded}
                >
                  <span className="task-details__label">
                    Komentari{notes.length > 0 ? ` (${notes.length})` : ''}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`task-details__chevron ${isNotesExpanded ? 'is-expanded' : ''}`}
                  />
                </button>

                <Collapsible open={isNotesExpanded}>
                  {notesLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <SkeletonText width="70%" />
                      <SkeletonText width="50%" />
                    </div>
                  ) : notesError ? (
                    <p className="task-details__value">{notesError}</p>
                  ) : notes.length === 0 ? (
                    <p className="task-details__value">Nema komentara</p>
                  ) : (
                    <div className="task-details__notes">
                      {notes.map((note) => (
                        <div key={note.id} className="task-details__note">
                          <div className="task-details__note-header">
                            <span className="task-details__note-author">
                              {note.employeeName || 'Nepoznat korisnik'}
                            </span>
                            <span className="task-details__note-date">
                              {formatDate(note.createdAt)} {formatTime(note.createdAt)}
                            </span>
                          </div>
                          <p className="task-details__note-text">{note.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Collapsible>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay */}
      {isSidePanelOpen && (
        <div
          className="side-panel-overlay"
          onClick={closePanel}
        />
      )}

      {/* Add Task Side Panel - 3 koraka (podaci, aktivnost, dodela) */}
      <div className={`tasks-side-panel tasks-add-task-panel ${isAddTaskPanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Novi zadatak
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={closeAddTaskPanel}
          />
        </div>

        <div className="side-panel-content">
          <div className="custom-progress-bar">
            <div className="progress-steps">
              {ADD_TASK_STEPS.map((label, index) => (
                <React.Fragment key={label}>
                  {index > 0 && (
                    <div className={`progress-line ${addTaskStep >= index ? 'completed' : ''}`}></div>
                  )}
                  <div className={`progress-step ${addTaskStep >= index ? 'completed' : ''} ${addTaskStep === index ? 'active' : ''}`}>
                    <div className="step-circle">
                      {addTaskStep > index ? <Checkmark size={16} /> : index + 1}
                    </div>
                    <div className="step-label">
                      <div className="step-title">{label}</div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {addTaskError && (
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={addTaskError}
              onCloseButtonClick={() => setAddTaskError(null)}
              hideCloseButton={false}
              lowContrast
              style={{ marginTop: '1rem' }}
            />
          )}

          {/* Korak 1: naslov, opis, datum i vreme */}
          {addTaskStep === 0 && (
            <div className="tasks-add-task__step">
              <TextInput
                id="add-task-title"
                labelText="Naslov zadatka *"
                placeholder="Unesite naslov zadatka..."
                value={addTaskTitle}
                onChange={(e) => setAddTaskTitle(e.target.value)}
                disabled={isCreatingTask}
              />
              <TextArea
                id="add-task-description"
                labelText="Opis zadatka"
                placeholder="Unesite opis zadatka..."
                value={addTaskDescription}
                onChange={(e) => setAddTaskDescription(e.target.value)}
                rows={3}
                disabled={isCreatingTask}
              />
              <div className="tasks-add-task__row">
                <TextInput
                  id="add-task-date"
                  type="date"
                  labelText="Datum izvršavanja *"
                  value={addTaskDate}
                  onChange={(e) => setAddTaskDate(e.target.value)}
                  disabled={isCreatingTask}
                />
                <div className="tasks-add-task__field">
                  <p className="tasks-add-task__label">Vreme izvršavanja *</p>
                  <div className="tasks-add-task__time">
                    <Select
                      id="add-task-hour"
                      labelText="Sati"
                      hideLabel
                      value={addTaskTime.split(':')[0]}
                      onChange={(e) => setAddTaskTime(`${e.target.value}:${addTaskTime.split(':')[1]}`)}
                      disabled={isCreatingTask}
                    >
                      {TASK_TIME_HOURS.map((hour) => (
                        <SelectItem key={hour} value={hour} text={hour} />
                      ))}
                    </Select>
                    <span className="tasks-add-task__time-separator">:</span>
                    <Select
                      id="add-task-minute"
                      labelText="Minuti"
                      hideLabel
                      value={addTaskTime.split(':')[1]}
                      onChange={(e) => setAddTaskTime(`${addTaskTime.split(':')[0]}:${e.target.value}`)}
                      disabled={isCreatingTask}
                    >
                      {TASK_TIME_MINUTES.map((minute) => (
                        <SelectItem key={minute} value={minute} text={minute} />
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <Button kind="secondary" onClick={closeAddTaskPanel} disabled={isCreatingTask}>
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  className="add-task-button"
                  onClick={() => goToAddTaskStep(1)}
                  disabled={isCreatingTask || !addTaskTitle.trim() || !addTaskDate || !addTaskTime}
                >
                  Dalje
                </Button>
              </div>
            </div>
          )}

          {/* Korak 2: aktivnost, oblast i nivo ozbiljnosti */}
          {addTaskStep === 1 && (
            <div className="tasks-add-task__step">
              <div>
                <p className="tasks-add-task__label">Aktivnost *</p>
                <div className="tasks-add-task__search-row">
                  <Search
                    size="md"
                    placeholder="Pretraži aktivnosti..."
                    labelText=""
                    value={addTaskActivitySearch}
                    onChange={(e) => {
                      setAddTaskActivitySearch(e.target.value);
                      setAddTaskActivityPage(1);
                    }}
                    onClear={() => {
                      setAddTaskActivitySearch('');
                      setAddTaskActivityPage(1);
                    }}
                    closeButtonLabelText="Obriši pretragu"
                  />
                  {filteredAddTaskActivities.length > ADD_TASK_ACTIVITY_PAGE_SIZE && (
                    <div className="tasks-add-task__pager">
                      <Button
                        kind="ghost"
                        size="md"
                        renderIcon={ChevronLeft}
                        onClick={prevAddTaskActivityPage}
                        hasIconOnly
                        disabled={addTaskActivityCurrentPage === 1}
                        iconDescription="Prethodna stranica"
                      />
                      <span className="tasks-add-task__pager-label">
                        {addTaskActivityCurrentPage}/{addTaskActivityTotalPages}
                      </span>
                      <Button
                        kind="ghost"
                        size="md"
                        renderIcon={ChevronRight}
                        onClick={nextAddTaskActivityPage}
                        hasIconOnly
                        disabled={addTaskActivityCurrentPage === addTaskActivityTotalPages}
                        iconDescription="Sledeća stranica"
                      />
                    </div>
                  )}
                </div>
              </div>

              {addTaskActivitiesError && (
                <InlineNotification
                  kind="error"
                  title="Greška"
                  subtitle={addTaskActivitiesError}
                  onCloseButtonClick={() => setAddTaskActivitiesError(null)}
                  hideCloseButton={false}
                  lowContrast
                />
              )}

              {addTaskActivitiesLoading ? (
                <div className="tasks-add-task__activity-grid">
                  {[...Array(6)].map((_, index) => (
                    <SkeletonPlaceholder key={index} style={{ height: '80px', borderRadius: '6px' }} />
                  ))}
                </div>
              ) : (
                <div
                  className="tasks-add-task__activity-grid"
                  style={{ opacity: addTaskActivityPageFading ? 0 : 1 }}
                >
                  {addTaskActivitiesOnPage.length === 0 ? (
                    <p className="tasks-add-task__grid-empty">
                      {addTaskActivitySearch
                        ? 'Nema aktivnosti koje odgovaraju pretrazi'
                        : 'Nema dostupnih aktivnosti'}
                    </p>
                  ) : (
                    addTaskActivitiesOnPage.map((activity) => {
                      const isSelected = String(addTaskActivityId) === String(activity.id);
                      const typeColor = getActivityTypeColor(activity.activityType);
                      return (
                        <button
                          key={activity.id}
                          type="button"
                          className={`tasks-add-task__activity-card ${isSelected ? 'selected' : ''}`}
                          style={{ borderLeftColor: typeColor }}
                          onClick={() => setAddTaskActivityId(String(activity.id))}
                          disabled={isCreatingTask}
                          aria-pressed={isSelected}
                        >
                          <span className="tasks-add-task__activity-title">{activity.name}</span>
                          <span className="tasks-add-task__activity-meta">
                            <span className="tasks-add-task__activity-type" style={{ color: typeColor }}>
                              {translateActivityType(activity.activityType)}
                            </span>
                            {activity.duration && (
                              <span className="tasks-add-task__activity-duration">{activity.duration} min</span>
                            )}
                          </span>
                          {isSelected && <span className="tasks-add-task__activity-check">✓</span>}
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              <div>
                <Select
                  id="add-task-area"
                  labelText="Oblast"
                  value={addTaskAreaId}
                  onChange={(e) => setAddTaskAreaId(e.target.value)}
                  disabled={isCreatingTask}
                >
                  <SelectItem value="" text="Sve oblasti" />
                  {areas.map((area) => (
                    <SelectItem key={area.id} value={area.id} text={area.name} />
                  ))}
                </Select>
                {areas.length === 0 && (
                  <p className="tasks-add-task__hint">Nema dostupnih oblasti.</p>
                )}
              </div>

              <div>
                <p className="tasks-add-task__label">Nivo ozbiljnosti *</p>
                <div className="tasks-add-task__severity-tags">
                  {SEVERITY_OPTIONS.map((option) => {
                    const isSelected = addTaskSeverity === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`tasks-severity-tag tasks-severity-tag--${option.tone} ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => setAddTaskSeverity(option.value)}
                        disabled={isCreatingTask}
                        aria-pressed={isSelected}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-actions">
                <Button
                  kind="secondary"
                  onClick={() => goToAddTaskStep(0)}
                  disabled={isCreatingTask}
                >
                  Nazad
                </Button>
                <Button
                  kind="primary"
                  className="add-task-button"
                  onClick={() => goToAddTaskStep(2)}
                  disabled={isCreatingTask || !addTaskActivityId}
                >
                  Dalje
                </Button>
              </div>
            </div>
          )}

          {/* Korak 3: dodela - svima ili jednom zaposlenom */}
          {addTaskStep === 2 && (
            <div className="tasks-add-task__step">
              <div className="tasks-add-task__summary">
                <p><strong>Naslov:</strong> {addTaskTitle}</p>
                <p><strong>Aktivnost:</strong> {selectedAddTaskActivity?.name || '-'}</p>
                <p><strong>Oblast:</strong> {addTaskAreaId ? (selectedAddTaskArea?.name || '-') : 'Sve oblasti'}</p>
                <p><strong>Datum i vreme:</strong> {addTaskDate} u {addTaskTime}</p>
                <p><strong>Nivo ozbiljnosti:</strong> {selectedAddTaskSeverity.label}</p>
                {getActivityEmployeeTypeNames(selectedAddTaskActivity).length > 0 && (
                  <p>
                    <strong>Prati:</strong>{' '}
                    {getActivityEmployeeTypeNames(selectedAddTaskActivity).join(', ')}
                  </p>
                )}
              </div>

              <div>
                <Toggle
                  id="add-task-assign-all"
                  labelText="Dodeli svima"
                  labelA="Ne"
                  labelB="Da"
                  toggled={addTaskAssignToAll}
                  onToggle={handleAddTaskAssignAllToggle}
                  disabled={isCreatingTask}
                />
              </div>

              {!addTaskAssignToAll && (
                <>
                  <p className="tasks-add-task__label">Zaposleni *</p>
                  <Search
                    size="md"
                    placeholder="Pretražite zaposlene..."
                    labelText=""
                    value={addTaskEmployeeSearch}
                    onChange={(e) => handleAddTaskEmployeeSearch(e.target.value)}
                    onClear={() => handleAddTaskEmployeeSearch('')}
                    closeButtonLabelText="Obriši pretragu"
                  />

                  {addTaskEmployeesError && (
                    <InlineNotification
                      kind="error"
                      title="Greška"
                      subtitle={addTaskEmployeesError}
                      onCloseButtonClick={() => setAddTaskEmployeesError(null)}
                      hideCloseButton={false}
                      lowContrast
                    />
                  )}
                  {addTaskEmployeesLoading ? (
                    <div className="tasks-add-task__options">
                      {[...Array(3)].map((_, index) => (
                        <SkeletonText key={index} style={{ width: '100%', height: '48px' }} />
                      ))}
                    </div>
                  ) : addTaskEmployees.length === 0 ? (
                    <p className="tasks-add-task__empty">Nema zaposlenih</p>
                  ) : (
                    <>
                      <div
                        className="tasks-add-task__options"
                        style={{
                          opacity: addTaskEmployeePageFading ? 0 : 1,
                          transition: 'opacity 0.3s ease'
                        }}
                      >
                        {addTaskEmployees.map((employee) => {
                          const isSelected = addTaskEmployee?.id === employee.id;
                          return (
                            <button
                              key={employee.id}
                              type="button"
                              className={`task-assign-employee-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => setAddTaskEmployee(employee)}
                              disabled={isCreatingTask}
                            >
                              <div className="task-assign-employee-avatar">
                                {employee.imgSrc ? (
                                  <img
                                    src={employee.imgSrc}
                                    alt={`${employee.firstName} ${employee.lastName}`}
                                    className="task-assign-employee-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="task-assign-employee-initials"
                                  style={{ display: employee.imgSrc ? 'none' : 'flex' }}
                                >
                                  {`${employee.firstName?.charAt(0) || ''}${employee.lastName?.charAt(0) || ''}`.toUpperCase()}
                                </div>
                              </div>
                              <div className="task-assign-employee-info">
                                <span className="task-assign-employee-name">
                                  {employee.firstName} {employee.lastName}
                                </span>
                                <span className="task-assign-employee-role">
                                  {employee.employeeTypeName || '-'}
                                </span>
                              </div>
                              {isSelected && <Checkmark size={16} />}
                            </button>
                          );
                        })}
                      </div>

                      {addTaskEmployeeTotalPages > 1 && (
                        <div className="tasks-add-task__pagination">
                          <Button
                            kind="ghost"
                            size="md"
                            renderIcon={ChevronLeft}
                            onClick={prevAddTaskEmployeePage}
                            hasIconOnly
                            disabled={!addTaskEmployeeHasPrev}
                            iconDescription="Prethodna stranica"
                          />
                          <span className="tasks-pagination-label">
                            {addTaskEmployeePage}/{addTaskEmployeeTotalPages}
                          </span>
                          <Button
                            kind="ghost"
                            size="md"
                            renderIcon={ChevronRight}
                            onClick={nextAddTaskEmployeePage}
                            hasIconOnly
                            disabled={!addTaskEmployeeHasNext}
                            iconDescription="Sledeća stranica"
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <div className="form-actions">
                <Button
                  kind="secondary"
                  onClick={() => goToAddTaskStep(1)}
                  disabled={isCreatingTask}
                >
                  Nazad
                </Button>
                <Button
                  kind="primary"
                  className="add-task-button"
                  onClick={handleCreateTask}
                  disabled={isCreatingTask || (!addTaskAssignToAll && !addTaskEmployee)}
                >
                  {isCreatingTask ? 'Kreiranje...' : 'Kreiraj zadatak'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isAddTaskPanelOpen && (
        <div
          className="side-panel-overlay"
          onClick={closeAddTaskPanel}
        />
      )}

      {/* Assign Employee Side Panel */}
      <div className={`tasks-side-panel ${isAssignPanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Dodeli zaposlenog
            {selectedTaskIds.length > 0 && (
              <span style={{ fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary)', marginLeft: '0.5rem' }}>
                ({selectedTaskIds.length} zadataka)
              </span>
            )}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={closeAssignPanel}
          />
        </div>

        <div className="side-panel-content">
          <div style={{ marginBottom: '1rem' }}>
            <Search
              size="lg"
              placeholder="Pretražite zaposlene..."
              value={assignSearch}
              onChange={(e) => handleAssignSearchChange(e.target.value)}
              onClear={() => handleAssignSearchChange('')}
              labelText=""
              closeButtonLabelText="Obriši pretragu"
              style={{ width: '100%' }}
            />
          </div>

          {assignError && (
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={assignError}
              onCloseButtonClick={() => setAssignError(null)}
              hideCloseButton={false}
              lowContrast
              style={{ marginBottom: '1rem' }}
            />
          )}

          {assignLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[...Array(4)].map((_, index) => (
                <SkeletonText key={index} style={{ width: '100%', height: '48px' }} />
              ))}
            </div>
          ) : assignEmployees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--cds-text-secondary)' }}>
              Nema zaposlenih
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  opacity: assignPageFading ? 0 : 1,
                  transition: 'opacity 0.3s ease'
                }}
              >
                {assignEmployees.map((employee) => (
                  <button
                    key={employee.id}
                    type="button"
                    className="task-assign-employee-item"
                    onClick={() => handleAssignEmployee(employee)}
                    disabled={assigning}
                  >
                    <div className="task-assign-employee-avatar">
                      {employee.imgSrc ? (
                        <img
                          src={employee.imgSrc}
                          alt={`${employee.firstName} ${employee.lastName}`}
                          className="task-assign-employee-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="task-assign-employee-initials"
                        style={{ display: employee.imgSrc ? 'none' : 'flex' }}
                      >
                        {`${employee.firstName?.charAt(0) || ''}${employee.lastName?.charAt(0) || ''}`.toUpperCase()}
                      </div>
                    </div>
                    <div className="task-assign-employee-info">
                      <span className="task-assign-employee-name">
                        {employee.firstName} {employee.lastName}
                      </span>
                      <span className="task-assign-employee-role">
                        {employee.employeeTypeName || '-'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {assignTotalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                  <Button
                    kind="ghost"
                    size="md"
                    renderIcon={ChevronLeft}
                    onClick={prevAssignPage}
                    hasIconOnly
                    disabled={!assignHasPrev}
                    iconDescription="Prethodna stranica"
                  />
                  <span style={{ fontSize: '0.875rem', opacity: 0.8, minWidth: '60px', textAlign: 'center', fontWeight: '500' }}>
                    {assignPage}/{assignTotalPages}
                  </span>
                  <Button
                    kind="ghost"
                    size="md"
                    renderIcon={ChevronRight}
                    onClick={nextAssignPage}
                    hasIconOnly
                    disabled={!assignHasNext}
                    iconDescription="Sledeća stranica"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Assign Overlay */}
      {isAssignPanelOpen && (
        <div
          className="side-panel-overlay"
          onClick={closeAssignPanel}
        />
      )}

      {/* Assign Toast */}
      {assignToast.show && (
        <div className="toast-container">
          <ToastNotification
            kind={assignToast.kind}
            title={assignToast.title}
            subtitle={assignToast.subtitle}
            timeout={0}
            onClose={() => setAssignToast(prev => ({ ...prev, show: false }))}
            lowContrast
          />
        </div>
      )}

      {/* Add Task Toast (kreiranje zadatka) */}
      {addTaskToast.show && (
        <div className="toast-container">
          <ToastNotification
            kind={addTaskToast.kind}
            title={addTaskToast.title}
            subtitle={addTaskToast.subtitle}
            timeout={0}
            onClose={() => setAddTaskToast(prev => ({ ...prev, show: false }))}
            lowContrast
          />
        </div>
      )}

      {/* Floating Button "Označi sve" - vidljiv i na PC-u (admin) i na mobilnom */}
      {selectable && selectableVisibleTasks.length > 0 && (isDesktopView || selectedTaskIds.length > 0) && !isSidePanelOpen && !isAssignPanelOpen && ReactDOM.createPortal(
        <button
          className="floating-button tasks-select-all-fab"
          onClick={toggleSelectAllTasks}
          title={allTasksSelected ? 'Poništi izbor' : 'Označi sve'}
          style={{
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9999
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8da63d';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#9fb746';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            }}
          >
            <CheckboxChecked
              size={24}
              style={{
                flexShrink: 0,
                minWidth: '24px',
                minHeight: '24px',
                fill: '#ffffff'
              }}
              aria-label={allTasksSelected ? 'Poništi izbor' : 'Označi sve'}
              color="#ffffff"
            />
            <span style={{
              color: '#ffffff',
              fontWeight: '500',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              {allTasksSelected ? 'Poništi sve' : 'Označi sve'}
            </span>
          </button>,
          document.body
        )}

      {/* Floating Button "Filteri" - samo mobilni prikaz (kada nema selekcije) */}
      {selectable && !isDesktopView && selectedTaskIds.length === 0 && ReactDOM.createPortal(
        <button
          className="floating-button tasks-mobile-filter-btn"
          onClick={() => setIsMobileFilterOpen(true)}
          title="Filteri"
          style={{
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9998
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8da63d';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#9fb746';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            }}
          >
            <Filter
              size={24}
              style={{
                flexShrink: 0,
                minWidth: '24px',
                minHeight: '24px',
                fill: '#ffffff'
              }}
              aria-label="Filteri"
              color="#ffffff"
            />
            <span style={{
              color: '#ffffff',
              fontWeight: '500',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              Filteri
            </span>
          </button>,
          document.body
        )}

      {/* Mobile Filter Modal */}
      {isMobileFilterOpen && ReactDOM.createPortal(
        <div
          className={isFilterExiting ? 'filter-modal-exiting' : ''}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDarkMode ? 'rgba(22, 22, 22, 0.85)' : 'rgba(244, 244, 244, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-end',
            animation: isFilterExiting ? 'fadeOut 0.2s ease-out' : 'fadeIn 0.2s ease-out'
          }}
          onClick={handleCloseMobileFilter}
        >
          <div
            className="tasks-mobile-filter-panel"
            data-carbon-theme={theme}
            style={{
              width: '100%',
              backgroundColor: isDarkMode ? '#262626' : '#ffffff',
              borderRadius: '16px 16px 0 0',
              padding: '1.5rem',
              animation: isFilterExiting ? 'slideDownOut 0.3s ease-out' : 'slideUpIn 0.3s ease-out',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: isDarkMode ? '#ffffff' : '#161616' }}>Filteri</h3>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                className="tasks-mobile-filter-close-btn"
                renderIcon={Close}
                iconDescription="Zatvori"
                onClick={handleCloseMobileFilter}
                style={{ color: isDarkMode ? '#ffffff' : '#161616' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Select
                id="mobile-task-area-filter"
                labelText="Oblast"
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                size="md"
              >
                <SelectItem value="" text="Sve oblasti" />
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id} text={area.name} />
                ))}
              </Select>

              <Select
                id="mobile-task-activity-type-filter"
                labelText="Tip aktivnosti"
                value={activityTypeFilter}
                onChange={(e) => setActivityTypeFilter(e.target.value)}
                size="md"
              >
                <SelectItem value="" text="Svi tipovi aktivnosti" />
                {ACTIVITY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} text={t.label} />
                ))}
              </Select>

              <Select
                id="mobile-task-assigned-filter"
                labelText="Dodeljenost"
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value)}
                size="md"
              >
                <SelectItem value="0" text="Svi zadaci" />
                <SelectItem value="1" text="Samo dodeljeni" />
                <SelectItem value="2" text="Samo nedodeljeni" />
              </Select>

              <Button
                kind="primary"
                size="md"
                style={{ width: '100%', marginTop: '1rem' }}
                onClick={handleCloseMobileFilter}
              >
                Primeni filtere
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Floating Action Button - "Dodeli" za označene zadatke (PC i mobilni) */}
      {selectable && selectedTaskIds.length > 0 && !isAssignPanelOpen && !isSidePanelOpen && ReactDOM.createPortal(
        <button
          className="floating-button tasks-fab"
          onClick={openAssignPanel}
          title={`Dodeli ${selectedTaskIds.length} zadataka`}
          style={{
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9999
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
          }}
        >
          <User
            size={24}
            style={{
              flexShrink: 0,
              minWidth: '24px',
              minHeight: '24px',
              fill: '#ffffff'
            }}
            aria-label="Dodeli zadatke"
            color="#ffffff"
          />
          <span style={{
            color: 'white',
            fontWeight: '500',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            Dodeli ({selectedTaskIds.length})
          </span>
        </button>,
        document.body
      )}

    </PageContainer>
  );
};

export default Tasks;
